import '.ab{position:relative;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.eu{flex:1 1 auto;}.ssr-plugin'
import '.b{flex-direction:column;}.ssr-plugin'
import '.r{align-items:stretch;}.ssr-plugin'
import '.ax{justify-content:center;}.ssr-plugin'
import '.co{font-size:1.125rem;}.ssr-plugin'
import '.ev{appearance:none;}.ssr-plugin'
import '.ew{height:2.75rem;}.ssr-plugin'
import '.ex{padding:0.5rem 3rem 0.5rem 1rem;}.ssr-plugin'
import '.ey{text-overflow:ellipsis;}.ssr-plugin'
import '.cn{border-radius:0.75rem;}.ssr-plugin'
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons'
import { FocusEventHandler } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Style, styled } from 'inlines'
import { InputField } from '../InputField'

export type SelectFlatOption<T> = { value: T; label: string }
export type SelectOptionGroup<T> = { groupLabel: string; options: SelectFlatOption<T>[] }
export type SelectOption<T> = SelectFlatOption<T> | SelectOptionGroup<T>

function isOptionGroup<T>(item: SelectOption<T>): item is SelectOptionGroup<T> {
  return 'groupLabel' in item
}

function flattenOptions<T>(options: SelectOption<T>[]): SelectFlatOption<T>[] {
  return options.flatMap((item) => (isOptionGroup(item) ? item.options : [item]))
}

export type SelectProps<T extends string | number> = {
  label?: string
  labelStyle?: Style
  tooltipText?: string
  containerStyle?: Style
  value?: T | null
  setValue: (value: T | null) => void
  options: SelectOption<T>[]
  notSelectedLabel?: string
  variant?: 'outlined' | 'muted-blue'
  errorMessage?: string
  onBlur?: FocusEventHandler<HTMLSelectElement>
}

export const NOT_SELECTED_KEY = 'not-selelcted'

export const Select = <T extends string | number>({
  value,
  label,
  labelStyle,
  tooltipText,
  containerStyle,
  options,
  notSelectedLabel,
  variant = 'outlined',
  setValue,
  errorMessage,
  onBlur,
}: SelectProps<T>) => {
  const hasError = !!errorMessage
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const raw = e.target.value
    if (raw === NOT_SELECTED_KEY) {
      setValue(null)
      return
    }
    const flat = flattenOptions(options)
    const match = flat.find((o) => String(o.value) === raw)
    setValue(match ? match.value : null)
  }

  return (
    <InputField
      label={label}
      labelStyle={labelStyle}
      errorMessage={errorMessage}
      style={containerStyle}
      tooltipText={tooltipText}
    >
      <div className="ab a eu b r ax"
      >
        <styled.select className="co ev ew ex ey cn"
          value={
            value !== null && value !== undefined
              ? String(value)
              : NOT_SELECTED_KEY
          }
          onChange={handleChange}
          style={{
            border: `1px solid ${hasError ? 'var(--brand-primary)' : variant === 'outlined' ? 'var(--palette-blue-300)' : 'var(--palette-blue-50)'}`,
            background: hasError
              ? 'var(--status-error-muted)'
              : variant === 'outlined'
                ? 'var(--palette-white)'
                : 'var(--palette-blue-50)',
            '&:focus': {
              outline: 'none',
            },
            color:
              value !== null && value !== undefined
                ? 'var(--brand-secondary)'
                : 'var(--gray-300)',
          }}
          onBlur={onBlur}
        >
          {notSelectedLabel && (
            <styled.option value={NOT_SELECTED_KEY}>
              {notSelectedLabel}
            </styled.option>
          )}
          {options.map((item, index) =>
            isOptionGroup(item) ? (
              <optgroup key={index} label={item.groupLabel}>
                {item.options.map((option) => (
                  <styled.option
                    key={option.value}
                    value={String(option.value)}
                  >
                    {option.label}
                  </styled.option>
                ))}
              </optgroup>
            ) : (
              <styled.option
                key={item.value ?? NOT_SELECTED_KEY}
                value={String(item.value)}
              >
                {item.label}
              </styled.option>
            ),
          )}
        </styled.select>
        <FontAwesomeIcon
          icon={faChevronDown}
          color={
            variant === 'outlined'
              ? 'var(--palette-blue-200)'
              : 'var(--brand-secondary)'
          }
          style={{
            position: 'absolute',
            right: '1rem',
            pointerEvents: 'none',
            height: '1.125rem',
            width: '1.125rem',
          }}
        />
      </div>
    </InputField>
  )
}
